/**
 * Phase 14 Plan 14-02 — CheckerContextPanel
 *
 * Displays the checker's review context prominently above the entry detail (D-130).
 * Highlights checker name, checked-at timestamp, and optional checker remarks.
 */

import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { CheckerInfo } from '@/types/logsheet-approver';

export interface CheckerContextPanelProps {
  readonly checker: CheckerInfo;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function CheckerContextPanel({ checker }: CheckerContextPanelProps): JSX.Element {
  return (
    <Card
      className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
      data-testid="checker-context-panel"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
          <Badge
            variant="outline"
            className="border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-200"
          >
            Checker Review
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <span className="text-xs text-muted-foreground">Checked by</span>
          <div className="font-medium" data-testid="checker-context-panel-checker-name">
            {checker.checkerName}
          </div>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Checked at</span>
          <div className="font-mono text-xs" data-testid="checker-context-panel-checked-at">
            {formatDateTime(checker.checkedAt)}
          </div>
        </div>
        {checker.checkerRemarks !== null && checker.checkerRemarks !== '' ? (
          <div>
            <span className="text-xs text-muted-foreground">Checker remarks</span>
            <div
              className="whitespace-pre-wrap text-sm"
              data-testid="checker-context-panel-checker-remarks"
            >
              {checker.checkerRemarks}
            </div>
          </div>
        ) : (
          <div>
            <span className="text-xs text-muted-foreground">Checker remarks</span>
            <div
              className="italic text-muted-foreground"
              data-testid="checker-context-panel-checker-remarks"
            >
              No remarks provided.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
